import userflow from 'userflow.js';
import CONSTANTS from 'shared/constants';

// CIN-7217. Userflow replaces Pendo for in-app guides, checklists and
// announcements. Identity is pushed from shared/analytics/use-analytics-identity;
// this module owns whether and when Userflow loads.
//
// Init is deferred to the first identify rather than done at module load. That
// is what keeps signed-out visitors out of Userflow entirely: every session
// starts on a public page, so an init gated on the pathname at load time would
// either disable Userflow for the whole session after a normal sign-in, or
// load the SDK for people who never sign in. Nothing is requested from
// Userflow's CDN until an authenticated user is identified.

const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
const isInIframe = (() => {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws — that itself proves we're
    // embedded on a different-origin parent (i.e., an external site).
    return true;
  }
})();

// LOCAL is off by default so dev logins don't count toward Userflow MAU, but
// building or checking a flow needs a real session — localStorage lets a
// developer opt in without a rebuild.
const localOptIn = (() => {
  try {
    return window.localStorage?.getItem('userflow_debug') === 'true';
  } catch {
    return false;
  }
})();

// Surfaces that never become an authenticated app session, so they can be ruled
// out up front: email recipients on /unsubscribe never log in, and the customer
// drawer embedded in an iframe is a partner's page, not ours.
const enabled = !!USERFLOW_TOKEN && !pathname.startsWith('/unsubscribe/') && !(pathname.startsWith('/customer-drawer') && isInIframe) && (CONSTANTS.ENV !== 'LOCAL' || localOptIn);
let initialized = false;
let pendingNavigate = null;
const ensureInit = () => {
  if (!enabled) return false;
  if (!initialized) {
    userflow.init(USERFLOW_TOKEN);
    initialized = true;
    if (pendingNavigate) {
      userflow.setCustomNavigate(pendingNavigate);
    }
  }
  return true;
};

/**
 * Run a Userflow call, swallowing anything it throws.
 *
 * Userflow's loader rejects when its CDN script cannot load — an ad blocker, an
 * offline CDN — and an unhandled rejection from a guidance tool is not
 * something a user should ever see. Every entry point goes through here so a
 * blocked script degrades to "no guides" rather than an app error.
 */
const call = (fn, {
  init = true
} = {}) => {
  if (init ? !ensureInit() : !initialized) return Promise.resolve();
  try {
    return Promise.resolve(fn()).catch(e => {
      console.warn('Userflow call failed:', e);
    });
  } catch (e) {
    console.warn('Userflow call failed:', e);
    return Promise.resolve();
  }
};
export const identify = (userId, attributes, options) => call(() => userflow.identify(userId, attributes, options));
export const updateUser = attributes => call(() => userflow.updateUser(attributes), {
  init: false
});
export const group = (groupId, attributes, options) => call(() => userflow.group(groupId, attributes, options));

/**
 * Clear the identity so the next person on this browser is not treated as the
 * previous one. A no-op when nobody was ever identified, which is why it does
 * not initialize.
 */
export const reset = () => call(() => userflow.reset(), {
  init: false
});

/**
 * Route in-flow "Go to page" actions through the app's router instead of a full
 * page load. Held until init so that wiring it does not itself load the SDK.
 */
export const setCustomNavigate = navigate => {
  pendingNavigate = navigate;
  if (initialized) {
    call(() => userflow.setCustomNavigate(navigate), {
      init: false
    });
  }
};
export const isIdentified = () => initialized && userflow.isIdentified();
export const isEnabled = () => enabled;
export default {
  identify,
  updateUser,
  group,
  reset,
  setCustomNavigate,
  isIdentified,
  isEnabled
};